import React, { useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useLang } from '../../LangContext';
import { getCartKey } from './cartUtils';

const Reveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, delay }}
    >
      {children}
    </motion.div>
  );
};

const TibebPattern = () => (
  <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, zIndex: 0, opacity: 0.04, pointerEvents: 'none' }}>
    <pattern id="tibeb" x="0" y="0" width="120" height="120" patternUnits="userSpaceOnUse">
      <path d="M60 0 L120 60 L60 120 L0 60 Z" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <circle cx="60" cy="60" r="15" fill="none" stroke="currentColor" strokeWidth="0.5" />
      <path d="M0 0 L120 120 M120 0 L0 120" stroke="currentColor" strokeWidth="0.2" opacity="0.5" />
    </pattern>
    <rect width="100%" height="100%" fill="url(#tibeb)" />
  </svg>
);

function loadCart(sellerId) {
  const key = getCartKey(sellerId);
  if (!key) return [];
  try { return JSON.parse(localStorage.getItem(key)) || []; }
  catch { return []; }
}
function saveCart(sellerId, cart) {
  const key = getCartKey(sellerId);
  if (!key) return;
  localStorage.setItem(key, JSON.stringify(cart));
}

function getToken() {
  return localStorage.getItem('buyerToken') || localStorage.getItem('userToken') || null;
}

export default function CartPage() {
  const { seller_id } = useParams();
  const navigate = useNavigate();
  const { t } = useLang();

  const [items, setItems] = useState(() => loadCart(seller_id));
  const token = getToken();
  const sellerName = items.length > 0 ? items[0].seller_name : null;
  const cartTotal = items.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0);

  const handleQuantityChange = (productId, rawValue) => {
    const qty = Math.max(1, parseInt(rawValue, 10) || 1);
    const updated = items.map(item => item.product_id === productId ? { ...item, quantity: qty } : item);
    setItems(updated);
    saveCart(seller_id, updated);
  };

  const handleRemove = (productId) => {
    const updated = items.filter(item => item.product_id !== productId);
    setItems(updated);
    saveCart(seller_id, updated);
  };

  const handleProceed = () => {
    if (!token) {
      navigate('/login', { state: { from: `/bazaar/cart/${seller_id}` } });
      return;
    }
    navigate(`/bazaar/checkout/${seller_id}`);
  };

  if (items.length === 0) {
    return (
      <div className="page" style={{ paddingTop: 100 }}>
        <div className="hero-gradient" style={{ height: '30vh' }} />
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <Reveal>
            <div className="creative-card" style={{ textAlign: 'center', padding: '64px 40px' }}>
              <div style={{ fontSize: 64, marginBottom: 24 }}>🛒</div>
              <h1 style={{ fontSize: 28, fontWeight: 900, marginBottom: 12 }}>Your Cart is Empty</h1>
              <p style={{ color: 'var(--text-muted)', marginBottom: 32 }}>Looks like you haven't added anything to your cart yet.</p>
              <Link to="/bazaar" className="btn-primary" style={{ display: 'inline-flex' }}>← Browse Bazaar</Link>
            </div>
          </Reveal>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ paddingTop: 100 }}>
      <div className="hero-gradient" style={{ height: '30vh' }} />
      <TibebPattern />

      <div style={{ maxWidth: 900, margin: '0 auto' }}>
        <Reveal>
          <Link to="/bazaar" style={{ color: 'var(--primary)', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, marginBottom: 32 }}>← Back to Bazaar</Link>
          <h1 style={{ fontSize: 'clamp(22px, 3.5vw, 30px)', fontWeight: 900, marginBottom: 8 }}>Your Cart</h1>
          {sellerName && <p style={{ color: 'var(--text-muted)', marginBottom: 40 }}>Store: <strong style={{ color: 'var(--secondary)' }}>{sellerName}</strong></p>}
        </Reveal>

        <div style={{ display: 'grid', gap: 32 }}>
          {/* Cart Items */}
          <Reveal delay={0.1}>
            <div className="creative-card">
              <h2 style={sectionTitleStyle}>📦 Items</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                {items.map(item => (
                  <div key={item.product_id} style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '16px 0', borderBottom: '1px solid rgba(0,0,0,0.05)' }}>
                    {item.photo_url ? (
                      <img src={item.photo_url} alt={item.name} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 16 }} />
                    ) : (
                      <div style={{ width: 80, height: 80, background: 'var(--bg-subtle)', borderRadius: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 32 }}>📦</div>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 800, color: 'var(--secondary)', fontSize: 18, marginBottom: 4 }}>{item.name}</div>
                      <div style={{ color: 'var(--text-muted)', fontSize: 14 }}>Unit: <span style={{ color: 'var(--primary)', fontWeight: 700 }}>ETB {Number(item.price).toLocaleString()}</span></div>
                    </div>
                    <div style={{ textAlign: 'center' }}>
                      <label style={miniLabelStyle}>Qty</label>
                      <input type="number" min="1" value={item.quantity} onChange={e => handleQuantityChange(item.product_id, e.target.value)} style={qtyInputStyle} />
                    </div>
                    <div style={{ textAlign: 'right', minWidth: 100 }}>
                      <label style={miniLabelStyle}>Total</label>
                      <div style={{ color: 'var(--secondary)', fontWeight: 900, fontSize: 18 }}>ETB {(Number(item.price) * item.quantity).toLocaleString()}</div>
                    </div>
                    <button type="button" onClick={() => handleRemove(item.product_id)} style={removeBtnStyle}>✕</button>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 24, paddingTop: 24, borderTop: '2px solid var(--bg-subtle)' }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: 'var(--text-muted)' }}>Cart Total</span>
                <span style={{ fontSize: 32, fontWeight: 900, color: 'var(--primary)' }}>ETB {cartTotal.toLocaleString()}</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <button
              onClick={handleProceed}
              className="btn-primary"
              style={{ width: '100%', justifyContent: 'center', padding: '20px 0', fontSize: 18 }}
            >
              {t.proceedToCheckout} — ETB {cartTotal.toLocaleString()}
            </button>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

const sectionTitleStyle = { fontSize: 20, fontWeight: 900, color: 'var(--secondary)', marginBottom: 24, marginTop: 0 };
const miniLabelStyle = { display: 'block', fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 8 };
const qtyInputStyle = { width: 64, padding: '10px', background: 'var(--bg-subtle)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 12, textAlign: 'center', fontWeight: 800, color: 'var(--secondary)' };
const removeBtnStyle = { background: 'none', border: '1px solid rgba(239, 68, 68, 0.2)', color: '#ef4444', borderRadius: 10, width: 36, height: 36, cursor: 'pointer', transition: 'all 0.2s' };
